import * as signalR from "@microsoft/signalr";
import { createAction } from "@reduxjs/toolkit";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { updateAllStoresStates } from "shared/hooks/useNetworkStatus";
import { useHubConnection } from "../context/HubConnectionContext";
import { methodMapFun } from "../methods/journey-map";
import { methodWorkspaceFun } from "../methods/workspace";

export const useSignalR = ({ name,updateConnection  }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const userInfo = useSelector(({ auth }) => auth.userInfo);

    useEffect(() => {

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`api/${name}`, {
                accessTokenFactory: () => token,
            })
            .withAutomaticReconnect([5000]) // Retry intervals
            .configureLogging(signalR.LogLevel.Error)
            .build();

        let shouldReconnect = true; // Flag to control reconnection attempts
        let reconnectTimeout = null; // Holds the timeout ID for throttling reconnects
        let attempt = 0;
        let maxAttempt = 10;

        const setupConnectionHandlers = () => {
         

            connection.on("OnWorkspaceUpdate", (methodName, args) => {
                methodWorkspaceFun(methodName, args, dispatch,userInfo?.UserId)
            }
            );

            connection.onclose((error) => {
                console.error("SignalR connection closed:", error);
                dispatch(createAction("ONLINE_USER_REGISTRY_LOGOUT_CLOSE")({ userIds: [] }));
                if (shouldReconnect) attemptReconnect();
            });

            connection.onreconnecting((error) => {
                console.warn("SignalR reconnecting due to error:", error);
            });

            connection.onreconnected(async (connectionId) => {            
               // console.log("SignalR reconnected with connectionId:", connectionId);
                updateConnection(connection);
                reinitializeConnection();
            });
        };

        const startConnection = async () => {
            try {
                if (navigator.onLine) {
                    if (connection.state !== signalR.HubConnectionState.Disconnected) {
                        //console.log("Connection is not in 'Disconnected' state. Stopping first...");
                        await connection.stop(); // Ensure the connection is stopped before starting
                    }
        
                    await connection.start();
                    //console.log("SignalR connected");
        
                    
                    reinitializeConnection();
                    updateConnection(connection);
                    return true

                } else {
                    console.warn("Browser is offline. Delaying SignalR connection...");
                }
            } catch (error) {
                console.error("Error starting SignalR connection:", error);
                if (shouldReconnect) {
                    clearTimeout(reconnectTimeout);
                    reconnectTimeout = setTimeout(attemptReconnect, 5000); // Retry after 5 seconds
                }
                return false
            }
        };
        

        const attemptReconnect = async () => {
            if (
                navigator.onLine && 
                connection.state === signalR.HubConnectionState.Disconnected
            ) {
                //console.log("Attempting to reconnect SignalR...");
                attempt++
                const result  = await startConnection();
                if(result) {
                    await reinitializeConnection();
                await updateAllStoresStates(dispatch);
                }
           


            } else if (connection.state !== signalR.HubConnectionState.Disconnected) {
                console.warn(
                    "Connection is not in a 'Disconnected' state. Current state:", 
                    connection.state
                );
            }

        };
        

        const handleOnline = () => {
            //console.log("Browser is back online. Checking SignalR connection...");
            shouldReconnect = true;
            attempt = 0;
            attemptReconnect();
        };

        const handleOffline = () => {
            console.warn("Browser is offline. Stopping SignalR connection...");
            shouldReconnect = false; // Prevent automatic retries while offline
            if (connection.state === signalR.HubConnectionState.Connected) {
                connection.stop().catch((err) =>
                    console.error("Error stopping SignalR connection on offline:", err)
                );
            }
        };

        const reinitializeConnection = async () => {
            try {
                if (connection.state === signalR.HubConnectionState.Connected) {
                    await connection.invoke("UserLogin", userInfo?.UserId);
                    //console.log("Reinitialized SignalR connection");
                }
            } catch (error) {
                console.error("Error during reinitialization after reconnection:", error);
            }
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                //console.log("User resumed activity. Checking SignalR connection...");
                shouldReconnect = true;
                attempt = 0;
                
                attemptReconnect();

            }
        };

        setupConnectionHandlers();
        startConnection();


        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        //.log("SignalR connection initialized");

        return () => {
            clearTimeout(reconnectTimeout);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            shouldReconnect = false; // Stop reconnection attempts
            connection.stop().catch((err) =>
                console.error("Error stopping SignalR connection during cleanup:", err)
            );
        };
    }, [name, token, userInfo, dispatch]);

    return null;
};


export const useHubMapConnection = ({ forceReload }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { cjmIdStr, personaIdStr } = useLocation().search.getAllMapsParams();
    const { connection } = useHubConnection();
    const userInfo = useSelector((state) => state.auth.userInfo);

    // Track connection instance and previous state
    const connectionRef = useRef(null);
    const prevConnectionState = useRef(signalR.HubConnectionState.Disconnected);

    const wrapperMethod = (methodName, args) => {
        methodMapFun(
            methodName,
            args,
            dispatch,
            cjmIdStr,
            personaIdStr,
            userInfo?.UserId,
            history,
            forceReload
        );
    }
    // , [dispatch, cjmIdStr, personaIdStr, userInfo?.UserId, history, forceReload]);

    const setupHandlers = useCallback(async () => {
        if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
            // console.warn("⏳ Waiting for SignalR to connect before setting up handlers...");
            return;
        }

        // console.log("✅ SignalR is connected. Setting up event handlers...");
        connectionRef.current.on("OnMapUpdate", wrapperMethod);

        // console.log("📡 Invoking UserLoginToMap...");
        try {
            await connectionRef.current.invoke("UserLoginToMap", cjmIdStr);
            // console.log("✅ UserLoginToMap invoked successfully.");
        } catch (error) {
            console.error("❌ Error in UserLoginToMap:", error);
        }
    }, [wrapperMethod, cjmIdStr]);

    useEffect(() => {
        if (!connection) return;

        connectionRef.current = connection;
        const currentState = connection.state;

        //console.log(`🔄 SignalR state changed: ${currentState} (prev: ${prevConnectionState.current})`);

        if (
            (prevConnectionState.current === signalR.HubConnectionState.Disconnected &&
                currentState === signalR.HubConnectionState.Connected) ||
            (prevConnectionState.current === signalR.HubConnectionState.Connecting &&
                currentState === signalR.HubConnectionState.Connected)
        ) {
            // console.log("🔄 Reconnected after being disconnected/connecting, re-running setupHandlers...");
            setupHandlers();
        }

        // Store the current state for the next check
        prevConnectionState.current = currentState;
    }, [connection, setupHandlers]);

    return null;
};